import React, { useState } from 'react'
import Image from 'next/image'
import { ThumbsUp, MoreHorizontal, Flag, Trash2 } from 'lucide-react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { useDeleteRecipeCommentMutation, useAddRecipeCommentReactionMutation } from 'graphql/generated/graphql'

interface ReviewCardProps {
  userName: string
  userAvatar: string
  rating: number
  date: string
  reviewText: string
  reviewImage?: string
  likesCount?: number
  userid: any
  commentorId: number
  recipeOwnerId: number
  comment_ID: number
  handleDelete: () => void
  handleReaction: () => void
  isLiked: boolean
  localLikesCount: number
  isDeleted: boolean
  setIsLiked: (isLiked: boolean) => void
  setLocalLikesCount: (localLikesCount: number) => void
  canDelete: boolean
}

const maxStars = 5

const ReviewCard: React.FC<ReviewCardProps> = ({
  userName,
  userAvatar,
  rating,
  date,
  reviewText,
  reviewImage,
  likesCount = 0,
  userid,
  commentorId,
  recipeOwnerId,
  comment_ID,
  handleDelete,
  handleReaction,
  isLiked,
  localLikesCount,
  isDeleted,
  setIsLiked,
  setLocalLikesCount,
  canDelete,
}) => {

  return (
    <div className="w-full border-b border-gray-200 pb-4 mb-4 mt-4">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <Image
            src={userAvatar}
            alt={userName}
            width={36}
            height={36}
            className="rounded-full object-cover w-9 h-9"
          />
          <span className="font-semibold text-[17px] text-off-black">{userName}</span>
        </div>
        <div className="flex items-center gap-2 text-gray-400 text-sm">
          <span>{date}</span>
          {(canDelete) && (
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <MoreHorizontal className="w-5 h-5 cursor-pointer" />
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  className="min-w-[220px] bg-white rounded-md p-1 shadow-lg border border-gray-200"
                  sideOffset={5}
                >
                  {canDelete && (
                    <DropdownMenu.Item
                      className="flex items-center gap-2 px-4 py-2 text-[16px] text-red-600 hover:bg-red-50 rounded cursor-pointer font-medium focus:bg-red-50 focus:text-red-600 outline-none p-2 hover:border-none border-none"
                      onClick={handleDelete}
                    >
                      Remove Review
                    </DropdownMenu.Item>
                  )}
                  
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          )}
        </div>
      </div>
      <div className="mt-2 flex items-center gap-1">
        {[...Array(5)].map((_, index) => (
          <svg
            key={index}
            className={`h-6 w-6 ${index < rating ? 'text-yellow-400' : 'text-gray-200'}`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.967a1 1 0 00.95.69h4.18c.969 0 1.371 1.24.588 1.81l-3.385 2.46a1 1 0 00-.364 1.118l1.287 3.966c.3.921-.755 1.688-1.54 1.118l-3.385-2.46a1 1 0 00-1.175 0l-3.385 2.46c-.784.57-1.838-.197-1.539-1.118l1.287-3.966a1 1 0 00-.364-1.118l-3.385-2.46c-.783-.57-.38-1.81.588-1.81h4.18a1 1 0 00.95-.69l1.286-3.967z" />
          </svg>
        ))}
      </div>
      <div className="mt-2 text-[17px] text-off-black">
        {reviewText}
      </div>
      {reviewImage && (
        <div className="mt-4">
          <Image
            src={reviewImage}
            alt="Review image"
            width={300}
            height={200}
            className="rounded-lg object-cover w-64 h-48"
          />
        </div>
      )}
      <div className="mt-2 flex items-center gap-2 text-gray-400">
        <button 
          onClick={handleReaction}
          className={`flex items-center gap-1 ${isLiked ? 'text-[#85C349]' : ''}`}
        >
          <ThumbsUp className={`w-5 h-5 ${isLiked ? 'fill-[#85C349]' : ''}`} />
          <span className="text-[15px]">{localLikesCount}</span>
        </button>
      </div>
    </div>
  )
}

export default ReviewCard 